/**
 * About — the technical detail, in one place, on request.
 *
 * It shows no local agent, and no address, port or session id for a connected
 * instance — only its name (2.0.1 bug report, owner 2026-10-18). A local port
 * changes every launch and tells a user nothing they can act on; the rows keep
 * the facts a user can read and a support conversation asks for.
 *
 * **It reads the bridge itself rather than taking props.** Every value is
 * already available on `window.bimgo`, and the alternative is threading four
 * pieces of state through the titlebar, which renders outside the shell that
 * holds them. Self-contained also means the dialog is honest about the moment it
 * was opened, rather than about whenever its parent last re-rendered.
 *
 * Each row degrades on its own. A desktop too old to report plugins must still
 * show the version and the connected instances — a single failed lookup blanking the whole
 * dialog is exactly the wrong behaviour for the screen someone opens *because*
 * something is wrong.
 */
import { useEffect, useState } from "react";
import { useLocale } from "../i18n/LocaleProvider";
import { describeInstalledPlugins } from "../lib/about";
import { instanceLabel, sortInstances } from "../lib/hostStatus";
import { Modal } from "./settings/Modal";
import type { CadInstance, DetectSnapshot } from "../bimgo";

interface AppInfo {
  version: string;
  bridgeVersion: number;
  environment: string;
  platform: string;
  /** Absent when talking to an older desktop bridge; shown only when present. */
  arch?: string;
}

/**
 * What each lookup produced.
 *
 * `undefined` is "still loading" and `null` is "asked and could not answer" —
 * different states that must read differently, the same distinction the host
 * status keeps (Corrections #23). Collapsing them makes a slow read look like a
 * broken one.
 */
interface AboutData {
  info?: AppInfo | null;
  hosts?: CadInstance[] | null;
  plugins?: DetectSnapshot | null;
}

/** Read one bridge value; a failure is `null`, never a thrown dialog. */
async function read<T>(fn: (() => Promise<T>) | undefined): Promise<T | null> {
  if (!fn) return null;
  try {
    return await fn();
  } catch {
    return null;
  }
}

export function useAboutData(open: boolean): AboutData {
  const [data, setData] = useState<AboutData>({});

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Re-read on every open. These are live facts — Revit opens and closes, plugins
    // install and update — and a cached snapshot would be a stale
    // answer given with a straight face.
    setData({});

    const bridge = window.bimgo;
    void (async () => {
      const [info, hosts, plugins] = await Promise.all([
        read(bridge?.app?.info),
        read(bridge?.hosts?.list),
        read(bridge?.plugins?.detect),
      ]);
      if (cancelled) return;
      setData({
        info,
        hosts,
        // `detect` resolves to an outcome rather than rejecting: a refusal is a
        // state the pane renders, not an exception.
        plugins: plugins && plugins.ok ? plugins.result : null,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [open]);

  return data;
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="about-row">
      <div className="about-row-label">{label}</div>
      <div className="about-row-value">{children}</div>
    </div>
  );
}

export function AboutDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useLocale();
  const data = useAboutData(open);

  const loading = data.info === undefined;
  const instances = data.hosts ? sortInstances(data.hosts) : [];
  const plugins = describeInstalledPlugins(data.plugins);

  // The app's own Modal rather than a second overlay: it already owns Escape,
  // the backdrop click and the ✕, and a dialog that closed differently from
  // every other dialog in the app would be a small, permanent surprise.
  return (
    <Modal open={open} onClose={onClose} title={t("about.title")}>
      <div className="about-body">
        <Row label={t("about.desktop")}>
          {loading
            ? t("about.loading")
            : data.info
              ? `${data.info.version} · ${data.info.environment} · ${data.info.platform}${data.info.arch ? ` · ${data.info.arch}` : ""}`
              : t("about.unavailable")}
        </Row>

        <Row label={t("about.plugin")}>
          {loading ? (
            t("about.loading")
          ) : data.plugins === null ? (
            t("about.unavailable")
          ) : plugins.length === 0 ? (
            t("about.pluginNone")
          ) : (
            <ul className="about-list">
              {plugins.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </Row>

        <Row label={t("about.instances")}>
          {loading ? (
            t("about.loading")
          ) : data.hosts === null ? (
            // "Could not look" is not "looked and found none" — the one
            // distinction this whole status family exists to keep.
            t("about.unavailable")
          ) : instances.length === 0 ? (
            t("about.instancesNone")
          ) : (
            <ul className="about-list">
              {instances.map((instance) => (
                <li key={instance.sessionId}>{instanceLabel(instance, t("about.untitled"))}</li>
              ))}
            </ul>
          )}
        </Row>
      </div>
    </Modal>
  );
}
